import dayjs, { type Dayjs } from 'dayjs';
import type {
  AppAdminTenancySchemasContractContractOut,
  ContractRateIn,
} from '@/api/generated/schemas';
import {
  ContractRateKind,
  CycleKind,
  PaymentMoment,
  RateVariant,
} from '@/api/generated/schemas';

/** `ContractRateIn` with decimals narrowed to string (InputNumber
 * `stringMode`) and every field required — the form always sends the full
 * field set so the wholesale-replace PATCH never resets server values to
 * backend defaults. */
type ContractRateFormValue = Required<
  Pick<
    ContractRateIn,
    'kind' | 'variant' | 'cycle_kind' | 'payment_moment' | 'is_default'
  >
> & {
  value: string;
  min_consumption_threshold: string;
  max_consumption_threshold: string;
  max_billable_cycles: number;
  exclusive_period_months: number;
  cycle_length_days: number | null;
};

export type ContractFormValues = {
  publisher_id?: number | null;
  valid_from: Dayjs;
  rates: ContractRateFormValue[];
};

/** Common shape of `ContractCreateIn` (minus `organization_id`) and
 * `ContractUpdateIn` — the full field set is always sent, rates replaced
 * wholesale. */
type ContractPayload = {
  publisher_id: number | null;
  valid_from: string;
  rates: ContractRateIn[];
};

/** Contracts and publisher links start on any day after today — billing
 * settles today's usage as it arrives, so the backend refuses today. The
 * browser's day runs ahead of the backend's UTC one in Poland, never behind,
 * so this boundary is never looser than the backend's. */
export const isDisabledStartDate = (date: Dayjs) =>
  !date.isAfter(dayjs(), 'day');

// Threshold/cycle defaults mirror the backend `ContractRateIn` defaults.
export const CONTRACT_STARTER_RATE: Omit<ContractRateFormValue, 'value'> = {
  kind: ContractRateKind.regular,
  variant: RateVariant.general,
  min_consumption_threshold: '0.10',
  max_consumption_threshold: '0.95',
  max_billable_cycles: 1,
  // Regular rates carry no exclusivity window; switching `kind` to exclusive
  // makes the field required.
  exclusive_period_months: 0,
  cycle_kind: CycleKind.rolling,
  cycle_length_days: 365,
  payment_moment: PaymentMoment.progressive,
  // The first rate of a new contract is the default; the form's add button
  // overrides this to false so exactly one default is preserved.
  is_default: true,
};

export const toContractPayload = (
  values: ContractFormValues
): ContractPayload => ({
  // Explicit null so clearing the publisher works over PATCH.
  publisher_id: values.publisher_id ?? null,
  valid_from: values.valid_from.format('YYYY-MM-DD'),
  // Calendar cycles ignore the length; the disabled input keeps its last
  // value in form state, so null it out for an honest payload.
  rates: values.rates.map((rate) => ({
    ...rate,
    cycle_length_days:
      rate.cycle_kind === CycleKind.calendar ? null : rate.cycle_length_days,
    // Same story for the exclusivity window — only exclusive rates carry one,
    // 0 means none.
    exclusive_period_months:
      rate.kind === ContractRateKind.exclusive
        ? rate.exclusive_period_months
        : 0,
  })),
});

export const toContractFormValues = (
  contract: AppAdminTenancySchemasContractContractOut
): ContractFormValues => ({
  publisher_id: contract.publisher_id,
  valid_from: dayjs(contract.valid_from),
  // Explicit destructure drops `id`, which `ContractRateIn` doesn't accept.
  rates: contract.rates.map(
    ({
      kind,
      variant,
      value,
      min_consumption_threshold,
      max_consumption_threshold,
      max_billable_cycles,
      exclusive_period_months,
      cycle_kind,
      cycle_length_days,
      payment_moment,
      is_default,
    }) => ({
      kind,
      variant,
      value,
      min_consumption_threshold,
      max_consumption_threshold,
      max_billable_cycles,
      exclusive_period_months,
      cycle_kind,
      cycle_length_days,
      payment_moment,
      is_default,
    })
  ),
});
